import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "../../../Contexts/GlobalContext";
import {
  generateApiKey,
  getAllApiKeys,
  revokeApiKey,
  regenerateApiKey,
} from "../../../Actions/ApiKeyActions";
import { MdContentCopy, MdRefresh, MdDelete, MdAdd } from "react-icons/md";
import { HiKey } from "react-icons/hi";

const Developer = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { setSnackbar } = useGlobalContext();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewKeyForm, setShowNewKeyForm] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null);
  const [copiedKeyId, setCopiedKeyId] = useState(null);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await getAllApiKeys();
      if (response.success) {
        setApiKeys(response.data || []);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch API keys",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      setSnackbar({
        open: true,
        msg: "Please enter a key name",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await generateApiKey(newKeyName);
      if (response.success) {
        setNewlyGeneratedKey(response.data.apiKey);
        setNewKeyName("");
        setShowNewKeyForm(false);
        await fetchApiKeys();
        setSnackbar({
          open: true,
          msg: "API key generated successfully! Please save it securely.",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        msg: error.response?.data?.error?.message || "Failed to generate API key",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to revoke this API key? This action cannot be undone.")) {
      return;
    }

    try {
      setLoading(true);
      const response = await revokeApiKey(keyId);
      if (response.success) {
        await fetchApiKeys();
        setSnackbar({
          open: true,
          msg: "API key revoked successfully",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        msg: error.response?.data?.error?.message || "Failed to revoke API key",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to regenerate this API key? The old key will be revoked and cannot be used anymore.")) {
      return;
    }

    try {
      setLoading(true);
      const response = await regenerateApiKey(keyId);
      if (response.success) {
        setNewlyGeneratedKey(response.data.apiKey);
        await fetchApiKeys();
        setSnackbar({
          open: true,
          msg: "API key regenerated successfully! Please save the new key securely.",
          severity: "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        msg: error.response?.data?.error?.message || "Failed to regenerate API key",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyKey = (key, keyId) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(keyId);
    setSnackbar({
      open: true,
      msg: "API key copied to clipboard",
      severity: "success",
    });
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="w-full flex flex-col gap-6 h-full py-5">
      <div className="pb-6 border-b border-gray-200">
        <h1 className="text-[#0226BE] text-[28px] font-[600] mb-2">
          Developer API Keys
        </h1>
        <p className="text-gray-600 text-[14px]">
          Manage your API keys to access DIY platform programmatically
        </p>
      </div>

      {/* Newly Generated Key Display */}
      {newlyGeneratedKey && (
        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-yellow-900 font-[600] text-[16px] mb-2">
                ⚠️ Important: Save Your API Key
              </h3>
              <p className="text-yellow-800 text-[14px]">
                This is the only time you'll see this key. Make sure to copy and store it securely.
              </p>
            </div>
            <button
              onClick={() => setNewlyGeneratedKey(null)}
              className="text-yellow-900 hover:text-yellow-700"
            >
              ✕
            </button>
          </div>
          <div className="bg-white border border-yellow-300 rounded p-4 flex items-center justify-between">
            <code className="text-[13px] font-mono text-gray-800 flex-1 break-all">
              {newlyGeneratedKey}
            </code>
            <button
              onClick={() => handleCopyKey(newlyGeneratedKey, "new")}
              className="ml-4 p-2 bg-yellow-400 hover:bg-yellow-500 rounded transition-colors"
              title="Copy API key"
            >
              <MdContentCopy size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Generate New Key Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {!showNewKeyForm ? (
          <button
            onClick={() => setShowNewKeyForm(true)}
            disabled={loading || user?.role !== "admin"}
            className="flex items-center gap-2 bg-[#0226BE] text-white text-[14px] font-[500] py-2.5 px-5 rounded-md hover:bg-[#021ea8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdAdd size={20} />
            Generate New API Key
          </button>
        ) : (
          <form onSubmit={handleGenerateKey} className="space-y-4">
            <div>
              <label className="text-[#1a1a1a] text-[14px] font-[500] block mb-2">
                Key Name
              </label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., Production API Key, Development Key"
                className="border border-gray-300 h-[48px] text-[#1a1a1a] pl-4 pr-4 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0226BE] focus:border-[#0226BE] transition-all duration-200 w-full"
                required
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="bg-[#0226BE] text-white text-[14px] font-[500] py-2.5 px-5 rounded-md hover:bg-[#021ea8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Generating..." : "Generate"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewKeyForm(false);
                  setNewKeyName("");
                }}
                className="border-2 border-gray-300 text-gray-700 text-[14px] font-[500] py-2.5 px-5 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* API Keys List */}
      <div className="space-y-4">
        <h2 className="text-[#1a1a1a] text-[18px] font-[600]">
          Your API Keys
        </h2>
        {loading && apiKeys.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : apiKeys.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No API keys found. Generate your first API key to get started.
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="w-full overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-gray-700 text-[14px] font-[600] px-4 py-3">
                      Key Name
                    </th>
                    <th className="text-gray-700 text-[14px] font-[600] px-4 py-3">
                      Created
                    </th>
                    <th className="text-gray-700 text-[14px] font-[600] px-4 py-3">
                      Last Used
                    </th>
                    <th className="text-gray-700 text-[14px] font-[600] px-4 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((key) => (
                    <tr
                      key={key._id}
                      className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <HiKey className="text-gray-400" size={18} />
                          <span className="text-[#1a1a1a] font-[500] text-[14px]">
                            {key.keyName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-600 font-[400] text-[13px]">
                        {formatDate(key.createdAt)}
                      </td>
                      <td className="px-4 py-4 text-gray-600 font-[400] text-[13px]">
                        {formatDate(key.lastUsedAt)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleRegenerateKey(key._id)}
                            disabled={loading}
                            className="p-2 hover:bg-blue-100 rounded transition-colors disabled:opacity-50"
                            title="Regenerate key"
                          >
                            <MdRefresh
                              size={18}
                              className="text-blue-600"
                            />
                          </button>
                          <button
                            onClick={() => handleRevokeKey(key._id)}
                            disabled={loading}
                            className="p-2 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                            title="Revoke key"
                          >
                            <MdDelete size={18} className="text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* API Documentation Link */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-900 text-[14px]">
          <strong>Need help?</strong> Check out our{" "}
          <button
            onClick={() => navigate("/profile/api-documentation")}
            className="text-[#0226BE] font-[500] hover:underline bg-transparent border-none cursor-pointer p-0"
          >
            API documentation
          </button>{" "}
          to learn how to use these keys.
        </p>
      </div>
    </div>
  );
};

export default Developer;
